import { Content, LocationACL, ResourceGrant } from '@raydiant/api-client-js';
import Button from '@raydiant/raydial/components/Button';
import Dialog, {
  DialogHeader,
  DialogHeading,
  DialogSubheading,
} from '@raydiant/raydial/components/Dialog';
import Modal from '@raydiant/raydial/components/Modal';
import Text from '@raydiant/raydial/components/Text';
import { useCallback, useMemo, useState } from 'react';
import { selectUserProfile } from '../../../../../selectors/user';
import { useSelector } from 'react-redux';
import useCreateResourceACLV5 from '../../../../../hooks/useCreateResourceACLV5';
import { useToastState } from '@raydiant/raydial/components/Toast';
import useDeleteResourceACLV5 from '../../../../../hooks/useDeleteResourceACLV5';
import useUpdateResourceACLV5 from '../../../../../hooks/useUpdateResourceACLV5';
import Pill from '@raydiant/raydial/components/Pill';
import { User } from '@raydiant/raydial/components/Icons';
import ComboBox, { ComboBoxItem } from '@raydiant/raydial/components/ComboBox';
import { BACK_BUTTON_ICON } from '../../../../LocationsPage/DeviceShare/DeviceShare';
import UsersWithAccess from '../../../../LocationsPage/DeviceShare/UsersWithAccess';
import { selectDomainProfiles } from '../../../../../selectors/v2/domains';
import { changeV3DomainProfilesToV4 } from '../../../utils';
import {
  canDeleteContent,
  canEditContent,
  canShareContent,
} from '../../../../../utilities';

export interface ShareDialogProps {
  content: Content;
  isOpen: boolean;
  onClose: () => void;
}

export const GRANT_SELECT_OPTIONS = [
  {
    id: 'read',
    title: 'Viewer',
    description: 'View the content',
    disabled: false,
  },
  {
    id: 'update',
    title: 'Editor',
    description: 'View and edit the content',
    disabled: false,
  },
  {
    id: 'share',
    title: 'Access Manager',
    description: 'Editor rights plus share content',
    disabled: false,
  },
  {
    id: 'delete',
    title: 'Content Admin',
    description: 'All permissions, including delete content',
    disabled: false,
  },
];

const selectItemToGrants = (grant: ResourceGrant): ResourceGrant[] => {
  switch (grant) {
    case 'read':
      return ['read'];
    case 'update':
      return ['read', 'update'];
    case 'share':
      return ['read', 'update', 'share'];
    case 'delete':
      return ['read', 'update', 'share', 'delete'];
    default:
      return ['read'];
  }
};

export const deviceSelectItemToGrants = (
  grant: ResourceGrant,
): ResourceGrant[] => {
  switch (grant) {
    case 'read':
      return ['read'];
    case 'update':
      return ['read', 'update'];
    case 'publish':
      return ['read', 'update', 'publish'];
    case 'share':
      return ['read', 'update', 'share', 'publish'];
    case 'delete':
      return ['read', 'update', 'share', 'publish', 'delete'];
    default:
      return ['read'];
  }
};

export const deviceACLToSelectItem = (acl: LocationACL) => {
  switch (true) {
    case acl.deleteGrant:
      return 'delete';
    case acl.shareGrant:
      return 'share';
    case acl.customAccessGrants?.includes('publish'):
      return 'publish';
    case acl.updateGrant:
      return 'update';
    case acl.readGrant:
      return 'read';
    default:
      return 'read';
  }
};

export const aclToSelectItem = (acl: LocationACL) => {
  switch (true) {
    case acl.deleteGrant:
      return 'delete';
    case acl.fullGrant:
      return 'full';
    case acl.shareGrant:
      return 'share';
    case acl.updateGrant:
      return 'update';
    case acl.readGrant:
      return 'read';
    default:
      return 'read';
  }
};

const renderSubheading = (content: Content) => {
  switch (content.resourceType) {
    case 'folder':
      return 'And all content in the folder';
    case 'playlist':
      return 'And all content in the playlist';
    default:
      return '';
  }
};

const ShareDialog = ({ content, isOpen, onClose }: ShareDialogProps) => {
  const state = useToastState();

  const domainProfiles = useSelector(selectDomainProfiles);

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedGrant, setSelectedGrant] = useState<ResourceGrant>('read');
  const [shareWithDomain, setShareWithDomain] = useState(false);

  const currentUser = useSelector(selectUserProfile);
  const createResourceACL = useCreateResourceACLV5();
  const updateResourceACL = useUpdateResourceACLV5();
  const deleteResourceACL = useDeleteResourceACLV5();

  const availableGrants = useMemo(() => {
    if (!currentUser) return [];

    let defaultGrants = ['read'];

    if (canEditContent(currentUser, content)) {
      defaultGrants.push('update');
    }

    if (canShareContent(currentUser, content)) {
      defaultGrants.push('share');
    }

    if (canDeleteContent(currentUser, content)) {
      defaultGrants.push('delete');
    }

    return GRANT_SELECT_OPTIONS.map((grant) =>
      defaultGrants.includes(grant.id) ? grant : { ...grant, disabled: true },
    );
  }, [content, currentUser]);

  const v4DomainProfiles = useMemo(
    () => changeV3DomainProfilesToV4(domainProfiles),
    [domainProfiles],
  );

  const sortedDomainUsers = useMemo(
    () => [...v4DomainProfiles].sort((a, b) => a.name.localeCompare(b.name)),
    [v4DomainProfiles],
  );

  const selectedUsers = useMemo(
    () => sortedDomainUsers.filter((user) => selectedUserIds.includes(user.id)),
    [sortedDomainUsers, selectedUserIds],
  );

  const validUserList = sortedDomainUsers.filter(
    (user) =>
      user.id &&
      !selectedUserIds.includes(user.id) &&
      !content.acl.some((acl) => acl.grantProfile?.id === user.id) &&
      user.id !== content.ownedBy.id,
  );

  const isContentSharedWithDomain = content.acl.some(
    (acl) => acl.grantDomainId,
  );
  const showSelectedUsers = shareWithDomain || selectedUserIds.length > 0;

  const handleSelectUser = useCallback((value: string) => {
    if (value === 'domain') {
      setShareWithDomain(true);
      return;
    }
    setSelectedUserIds((prev) => {
      if (!prev.includes(value)) {
        return [...prev, value];
      }
      return prev;
    });
  }, []);

  const handleUnselectUser = useCallback((userId: string) => {
    setSelectedUserIds((prev) => prev.filter((id) => id !== userId));
  }, []);

  const handleReset = useCallback(() => {
    setSelectedUserIds([]);
    setSelectedGrant('read');
    setShareWithDomain(false);
  }, []);

  const handleShareContent = async () => {
    if (!currentUser) return;

    try {
      await createResourceACL.mutate({
        params: {
          resourceId: content.resourceId,
          grantProfileIds: !shareWithDomain ? selectedUserIds : [],
          grantDomainId: shareWithDomain ? currentUser?.domainId : '',
          grants: selectItemToGrants(selectedGrant),
        },
      });

      setSelectedUserIds([]);
      setSelectedGrant('read');
      setShareWithDomain(false);
      state.add({
        title: 'Content Shared',
        description: `${content.name} shared successfully`,
      });
    } catch (error) {
      state.add({
        title: 'Could not share content',
        description: 'Please try again',
        variant: 'caution',
      });
    }
  };

  const handleUpdateGrant = useCallback(
    async (acl: LocationACL, grant: ResourceGrant) => {
      if (!currentUser) return;

      try {
        await updateResourceACL.mutate({
          params: {
            resourceId: content.resourceId,
            grantProfileIds: acl.grantProfile ? [acl.grantProfile?.id] : [],
            grantDomainId: acl.grantDomainId ? acl.grantDomainId : '',
            grants: selectItemToGrants(grant),
          },
        });
        handleReset();
        state.add({
          title: 'Grant Updated',
          description: `grant updated successfully`,
        });
      } catch (error) {
        state.add({
          title: 'Could not update grant',
          description: 'Please try again',
          variant: 'caution',
        });
      }
    },
    [content.resourceId, currentUser, handleReset, state, updateResourceACL],
  );

  const handleCancel = () => {
    setSelectedUserIds([]);
    setShareWithDomain(false);
    setSelectedGrant('read');
  };

  const handleUnShareContent = async (acl: LocationACL) => {
    try {
      await deleteResourceACL.mutate({
        resourceId: content.resourceId,
        params: {
          grantProfileIds: acl.grantProfile?.id ? [acl.grantProfile.id] : [],
          grantDomainId: acl.grantDomainId ? acl.grantDomainId : '',
        },
      });
      handleCancel();
      state.add({
        title: 'User Access Removed',
        description: 'Access removed successfully',
      });
    } catch (error) {
      state.add({
        title: 'Could not remove user access',
        description: 'Please try again',
        variant: 'caution',
      });
    }
  };

  const renderSelectedUsers = () => (
    <div className="flex gap-2 flex-wrap pt-2">
      {shareWithDomain && (
        <Pill
          key="domain"
          className="text-lg"
          icon={<User size={16} />}
          showCloseIcon
          onClick={() => setShareWithDomain(false)}
          label="Everyone at domain"
        />
      )}
      {!shareWithDomain &&
        selectedUsers.map((user) => (
          <Pill
            key={user.id}
            className="text-lg"
            icon={<User size={16} />}
            showCloseIcon
            onClick={() => handleUnselectUser(user.id)}
            label={user.name}
          />
        ))}
    </div>
  );

  if (!currentUser) return null;
  return (
    <Modal isOpen={isOpen} className="text-black overflow-auto">
      <Dialog showCloseButton onClose={onClose}>
        <DialogHeader>
          <div className="flex gap-4 items-start">
            {showSelectedUsers && (
              <Button
                variant="unstyled"
                onPress={() => onClose()}
                className="mt-1"
              >
                {BACK_BUTTON_ICON}
              </Button>
            )}
            <div>
              <DialogHeading>
                <span>
                  Share '{content.name}' {content.resourceType}
                </span>
              </DialogHeading>
              <DialogSubheading>
                <Text>{renderSubheading(content)}</Text>
              </DialogSubheading>
            </div>
          </div>
        </DialogHeader>

        <div>
          <div className="flex flex-col">
            <div className="pt-4 pb-6">
              <ComboBox
                onSelectionChange={(key) => handleSelectUser(key as string)}
                selectedKey={''}
                placeholder="Select users"
                isDisabled={shareWithDomain || validUserList.length === 0}
                classNameForPopover="overflow-hidden md:!w-[585px]"
              >
                {!isContentSharedWithDomain && (
                  <ComboBoxItem id="domain" key="domain" textValue="domain">
                    Everyone at domain
                  </ComboBoxItem>
                )}
                {validUserList.map((user) => (
                  <ComboBoxItem
                    key={user.id}
                    id={user.id}
                    textValue={user.name}
                  >
                    {user.name}
                  </ComboBoxItem>
                ))}
              </ComboBox>
              {renderSelectedUsers()}
            </div>

            {!showSelectedUsers ? (
              <div>
                <UsersWithAccess
                  owner={content.ownedBy}
                  contentType={content.resourceType}
                  grantOptions={availableGrants}
                  locationACLs={content.acl}
                  domainUsers={sortedDomainUsers}
                  onRemove={handleUnShareContent}
                  onUpdate={handleUpdateGrant}
                />
                <div className="mt-8 md:px-6 pb-4">
                  <Button onPress={onClose} variant="primary" fullWidth>
                    Done
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col">
                <ComboBox
                  label="Access Level"
                  onSelectionChange={(key) =>
                    setSelectedGrant(key as ResourceGrant)
                  }
                  selectedKey={selectedGrant}
                  isReadOnly
                >
                  {availableGrants.map((grant) => (
                    <ComboBoxItem
                      key={grant.id}
                      id={grant.id}
                      textValue={grant.title}
                      isDisabled={grant.disabled}
                      checkMarkSelected
                    >
                      <div className="cursor-pointer">
                        <div>{grant.title}</div>
                        <div className="text-sm text-gray-600 h-3.5">
                          {grant.description}
                        </div>
                      </div>
                    </ComboBoxItem>
                  ))}
                </ComboBox>
                <div className="mt-8 md:px-4 flex pb-4 gap-6">
                  <Button onPress={handleCancel} variant="secondary" fullWidth>
                    Cancel
                  </Button>
                  <Button
                    isDisabled={!showSelectedUsers}
                    onPress={handleShareContent}
                    variant="primary"
                    fullWidth
                  >
                    Share Now
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </Modal>
  );
};

export default ShareDialog;
